import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, useWindowDimensions, View } from 'react-native';

import { type AddressType, BodyText, theme, useDebounceFn } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { TipsGroupCustomTip } from '../../../../components';
import { useBagTips, useTippingEnabled } from '../../hooks';
import { BagTip } from '../BagTip';

// ────────────────────────────────────────────────────────────────────────────

export const BagTipping = forwardRef<View, BagTippingProps>((props, ref) => {
  const {
    restaurantId,
    selectedTip,
    availableTips,
    maxTipAmount,
    orderChannel,
    isDisabled,
    onTipChange,
  } = props;

  const { formatMessage } = useIntl();
  const { fontScale } = useWindowDimensions();
  const isFlexEnabled = useFeatureFlag('CELS-5522-flex-enabled');

  // ─── Tips Data ────────────────────────────────────────────────────────────

  const isTippingEnabled = useTippingEnabled({
    restaurantId,
    orderChannel,
    availableTips,
    maxTipAmount,
  });

  const tips = useBagTips({ maxTipAmount, availableTips });

  const hasSelectedPreDefinedTip = useMemo(() => {
    return tips.some((tip) => tip.amount === selectedTip);
  }, [selectedTip, tips]);

  // ─── State ────────────────────────────────────────────────────────────────

  const [isCustomTipSelected, setIsCustomTipSelected] = useState(!hasSelectedPreDefinedTip);

  const selectedTipValue = isCustomTipSelected ? 'custom' : selectedTip;
  const onCustomTipChangeDebounced = useDebounceFn(onTipChange, isFlexEnabled ? 750 : 0);

  // ─── Accessibility ────────────────────────────────────────────────────────

  const { firstTipsLine, secondTipsLine } = useMemo(() => {
    const firstLine = tips.filter((tip) => tip.value !== '0' && tip.value !== 'custom');

    const secondLine = tips.filter((tip) => tip.value === '0' || tip.value === 'custom');

    if (fontScale <= 1) {
      return {
        firstTipsLine: tips,
        secondTipsLine: [],
      };
    }

    return {
      firstTipsLine: firstLine,
      secondTipsLine: secondLine,
    };
  }, [fontScale, tips]);

  // ──────────────────────────────────────────────────────────────────────────

  const onTipValueChange = useCallback(
    (tipValue: string) => {
      onCustomTipChangeDebounced.cancel();

      const hasSelectedCustomTip = tipValue === 'custom';
      const tipAmount = tips.find((tip) => tip.value === tipValue)?.amount ?? 0;

      // Only trigger callback if the change was changed.
      if (selectedTip > 0 || (!hasSelectedCustomTip && tipAmount > 0)) {
        onTipChange(hasSelectedCustomTip ? 0 : tipAmount);
      }

      // without this timeout, the custom tip component will be initialized with the previous amount.
      setTimeout(() => {
        setIsCustomTipSelected(hasSelectedCustomTip);
      });
    },
    [tips, onCustomTipChangeDebounced, onTipChange, selectedTip],
  );

  // ──────────────────────────────────────────────────────────────────────────

  if (!isTippingEnabled || tips.length === 0) return null;

  const sectionTitle =
    orderChannel === 'delivery'
      ? formatMessage(messages.deliveryTipTitle)
      : formatMessage(messages.restaurantTipTitle);

  const sectionDescription =
    orderChannel === 'delivery'
      ? formatMessage(messages.deliveryTipDescription)
      : formatMessage(messages.restaurantTipDescription);

  return (
    <View ref={ref} style={isFlexEnabled ? styles.containerWithFlex : styles.container}>
      <View style={isFlexEnabled ? styles.titleContainerWithFlex : styles.titleContainer}>
        <BodyText
          style={isFlexEnabled ? styles.title : undefined}
          bold={isFlexEnabled}
          sizeMatch={['14']}
        >
          {sectionTitle}
        </BodyText>

        <BodyText sizeMatch={isFlexEnabled ? ['14'] : ['12']} style={styles.description}>
          {sectionDescription}
        </BodyText>
      </View>

      {/* ─── Standard tips ────────────────────────── */}

      <View style={styles.tipsContainer} accessibilityRole="radiogroup">
        {firstTipsLine.map((item) => {
          const { value, amount, label, helperText, accessibilityHint } = item;

          return (
            <BagTip
              key={value}
              value={value}
              amount={amount}
              selectedTipValue={selectedTipValue}
              label={label}
              helperText={helperText}
              accessibilityLabel={label}
              accessibilityHint={accessibilityHint}
              isDisabled={isDisabled}
              setValue={onTipValueChange}
            />
          );
        })}
      </View>

      {secondTipsLine.length > 0 ? (
        <View style={styles.tipsContainer} accessibilityRole="radiogroup">
          {secondTipsLine.map((item) => {
            const { value, amount, label, helperText, accessibilityHint } = item;

            return (
              <BagTip
                key={value}
                value={value}
                label={label}
                amount={amount}
                selectedTipValue={selectedTipValue}
                helperText={helperText}
                accessibilityLabel={label}
                accessibilityHint={accessibilityHint}
                isDisabled={isDisabled}
                setValue={onTipValueChange}
              />
            );
          })}
        </View>
      ) : null}

      {/* ─── Custom tip ───────────────────────────── */}

      {isCustomTipSelected ? (
        <TipsGroupCustomTip
          selectedTip={selectedTip}
          maxTipAmount={maxTipAmount}
          onChange={onCustomTipChangeDebounced}
        />
      ) : null}
    </View>
  );
});

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  deliveryTipTitle: {
    defaultMessage: 'Thank your driver with a tip',
    description: 'Bag | Tipping | Delivery tip title',
  },
  deliveryTipDescription: {
    defaultMessage:
      "Optional, 100% of the tip goes to your courier. To modify, select 'Custom' or another amount.",
    description: 'Bag | Tipping | Delivery tip description',
  },
  restaurantTipTitle: {
    defaultMessage: 'Restaurant tip',
    description: 'Bag | Tipping | Restaurant tip title',
  },
  restaurantTipDescription: {
    defaultMessage:
      'Tipping for great service is always appreciated. It’ll be shared across our team!',
    description: 'Bag | Tipping | Restaurant tip description',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    gap: theme.spacing['4'],
    paddingVertical: theme.spacing['6'],
    paddingHorizontal: theme.spacing['4'],
    marginHorizontal: -theme.spacing['4'],
    borderTopWidth: theme.spacing['2'],
    borderTopColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  containerWithFlex: {
    gap: theme.spacing['4'],
    padding: theme.spacing['4'],
    paddingVertical: theme.spacing['6'],
    backgroundColor: theme.colors.OPACITY.OATMEAL.DARK,
    borderRadius: theme.radius.large,
  },
  title: {
    textAlign: 'center',
    paddingBottom: theme.spacing['4'],
  },
  titleContainer: {
    gap: theme.spacing['1'],
  },
  titleContainerWithFlex: {
    gap: theme.spacing['2'],
  },
  description: {
    color: theme.colors.CHARCOAL,
  },
  tipsContainer: {
    flexDirection: 'row',
    gap: theme.spacing['2'],
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type BagTippingProps = {
  selectedTip: number;
  availableTips: ReadonlyArray<number | 'CUSTOM'>;
  restaurantId: string;
  maxTipAmount: number;
  orderChannel: AddressType;
  isDisabled: boolean;
  onTipChange: (tip: number) => void;
};
